/***
 * Created on 2023/10/17
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useMemo, useState} from 'react';
import {useTranslation, I18nextProvider} from "react-i18next";
import sharedI18n from "common/SharedI18n";
import {useOnChange, useLanguage} from "common/Hook";
import {getErrorMessage, getCurrentDomainRegion, convertToQNAPLang, getPrivacyPolicyUrl, URLHandler} from 'common/ServiceCommon';
import GdprDialog from "./gdpr_dialog";
import TextInput from "../common/form/text_input";
import PasswordInput from "../common/form/password_input";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import SmsActivation from "./sms_activation";
import EmailActivation from "./email_activation";
import AccountActivated from "./account_activated";
import Captcha from "../common/captcha";
import SocialAccount from "../common/social/social_account";
import SendActivationEmail from "./send_activation_email";
import SignupChinaComponent from "./signup_china_component";


const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // default: true
        },
    },
});

function SignupStandardComponent(props) {
    const { t, i18n } = useTranslation();
    useLanguage(i18n);
    const [showGDPR, setShowGDPR] = useState(false);
    const [disclaimer, setDisclaimer] = useState(false);
    const [passwordMatch, setPasswordMatch] = useState(false);
    const [isValidQid, setIsValidQid] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const {values, setValues, onChange} = useOnChange({
        qid: '',
        display_name: '',
        password: '',
        confirm_password: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        captcha: ''
    });
    const urlHandler = new URLHandler(location.href);

    const isValid = useMemo(()=> {
        return disclaimer && values?.qid && values.password && passwordMatch && isValidQid;
    }, [values.qid, values.password, values.confirm_password, disclaimer, passwordMatch, isValidQid]);

    const onServiceTermClick = () => {
        const url = `${window.acc_mf.common.APP_QNAP_URL}/${convertToQNAPLang(i18n.language)}}/legal/qnap-cloud-service-terms-and-conditions`;
        window.open(url, '_blank');
    }

    const onQIDBlur = (e) => {
        onChange(e);
        const qid_expr = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$|^\d{6,16}$/gm;
        const res = qid_expr.test(e.target.value);
        setIsValidQid(res);
    }

    return (
        <section className="common-page-wrapper common-page-wrapper--sign-up" data-show-sns={!urlHandler.isHideSocial()}>
            <div className="common-page-header">{t('@Create Account@')}</div>
            <div className="common-page-body">
                { !urlHandler.isHideSocial() &&
                    <div className="sign-up--sns">
                        <div className="sign-up--sns__subtitle">{t('@Sign up with social media@')}</div>
                        <div className="sign-up--sns__group">
                            <SocialAccount showCreateNotice={false}/>
                        </div>
                    </div>
                }

                <div className="sign-up--divide"></div>

                <div className="sign-up--regular">
                    <div className="sign-up--regular__subtitle">{t('@Sign up with email or phone@')}</div>
                    <div className="sign-up--regular__group">
                        <div className="comp-input !mb-24">
                            <TextInput label={t('@Display Name@')} placeholder={t('@Display Name@')} name='display_name' value={values.display_name} maxLength={20} onChange={onChange} />
                        </div>

                        <div className="comp-input !mb-24">
                            <TextInput label={t('@Email or Phone@')} placeholder={t('@Email or Phone@')} inputMode={"email"} name='qid' value={values.qid} onChange={onQIDBlur} required={true} pattern="^([a-zA-Z0-9_\-\.]+)@[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,63}|\d{6,16}$"/>
                        </div>


                        <PasswordInput
                            label={t('@Password@')}
                            placeholder={t('@Password@')}
                            name='password'
                            value={values.password}
                            onChange={onChange}
                            required={true}
                            showStrength={true}
                            ignoreRules={false}
                            showTip={true}/>

                        <PasswordInput
                            label={t('@Confirm Password@')}
                            placeholder={t('@Confirm Password@')}
                            name='confirm_password'
                            value={values.confirm_password}
                            onChange={onChange}
                            status={setPasswordMatch}
                            matchInput={values.password}
                            required={true} />

                        {/*<p className="text-error mt-10">錯誤訊息</p>*/}

                        <Captcha value={values.captcha} inputChangeHandler={onChange} resetValue={() => setValues(prev => ({...prev, captcha: ''}))}/>

                    </div>

                    <div className="common-form-group">
                        <div className="comp-checkbox">
                            <label className="q-checkbox">
                                <input type="checkbox" name="checkbox-terms" checked={disclaimer} onChange={()=>setDisclaimer(prev=>!prev)}/>
                                <span dangerouslySetInnerHTML={
                                    {__html: t('@I agree to myQNAPcloud Terms of Service and Privacy Policy.@').
                                        replace(`<a ng-click='showTermsOfUse()'>`, `<a href="${window.acc_mf.common.APP_QNAP_URL}/${convertToQNAPLang(i18n.language)}/legal/qnap-cloud-service-terms-and-conditions" target="_blank">`).
                                        replace(`<a ng-click='showPrivacyPolicy()'>`, `<a href="${getPrivacyPolicyUrl()}" target="_blank">`)}
                                }></span>
                            </label>
                        </div>
                    </div>
                    <button className="q-btn q-btn--primary--focus btn-medium btn-rounded-normal" disabled={!isValid} onClick={()=>setShowGDPR(true)}>{t('@Sign Up@')}</button>
                    <div className="sign-up--options">
                        <p className="!mb-10"><a href="/signin">{t('@Already have an account?@')}</a></p>
                        {!urlHandler.isHideActivationMail() &&
                            <p><a href="#send_activation_email" onClick={e=>{location.href="/signup#send_activation_email"; location.reload()}}>{t('@Have not received activation email?@')}</a></p>
                        }
                    </div>
                </div>
            </div>
            {showGDPR && <GdprDialog showModal={showGDPR} closeModal={()=>setShowGDPR(false)} data={values}/>}
        </section>
    );
}

export function SignupGlobalIndex() {
    const url = new URL(window.location.href);
    const region = getCurrentDomainRegion();

    const showComp = (hash) => {
        const comps = {
            '#send_activation_email': <SendActivationEmail />,
            '#phone_activation': <SmsActivation />,
            '#email_activation': <EmailActivation />,
            '#account_activated': <AccountActivated />
        }

        const signup = region === 'CHINA' ? <SignupChinaComponent /> : <SignupStandardComponent />

        return Object.keys(comps).includes(hash) ? comps[hash] : signup;
    }

    return (
        <I18nextProvider i18n={sharedI18n}>
            <QueryClientProvider client={queryClient}>
                {
                    // url.hash === '#phone_activation' ? <SmsActivation /> : <SignupStandardComponent />
                    showComp(url.hash)
                }
            </QueryClientProvider>
        </I18nextProvider>
    );
}

export default SignupGlobalIndex;